import { toast } from 'react-hot-toast';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useExamResult } from '../hooks/useExams';
import { useExam } from '../hooks/useExams';
import { 
  ArrowLeft, 
  Trophy, 
  Clock, 
  CheckCircle, 
  XCircle, 
  HelpCircle,
  RotateCcw,
  Home,
  Share2,
  Zap
} from 'lucide-react';
import { 
  FadeIn, 
  ScaleIn, 
  ConfettiEffect,
  ProgressBar,
  AnimatedCard 
} from '../components/ui/AnimatedCard';
import { useState } from 'react';

export function ExamResult() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const examId = parseInt(id || '0');
  
  const { data: result, isLoading } = useExamResult(examId);
  const { data: exam } = useExam(examId);
  const [showConfetti, setShowConfetti] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!result) {
    return (
      <FadeIn>
        <div className="text-center py-16">
          <HelpCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Result not found</h2>
          <p className="text-slate-500 mb-4">You haven't taken this exam yet</p>
          <Link
            to={`/exams/${examId}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium"
          >
            Take Exam
          </Link>
        </div>
      </FadeIn>
    );
  }

  const percentage = Math.round((result.score / result.totalMarks) * 100);
  const isPassed = percentage >= 40;
  const correctAnswers = result.answers?.filter((a: any) => a.isCorrect).length || 0;
  const totalQuestions = result.answers?.length || 0;
  const timeSpent = result.timeSpentSeconds || 0;
  const minutes = Math.floor(timeSpent / 60);
  const seconds = timeSpent % 60;

  return (
    <div className="max-w-3xl mx-auto">
      <ConfettiEffect trigger={showConfetti} />
      
      <FadeIn>
        <button
          onClick={() => navigate('/exams')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Exams
        </button>
      </FadeIn>

      {/* Result Card */}
      <ScaleIn>
        <div className={`rounded-2xl p-8 text-center mb-6 ${
          isPassed 
            ? 'bg-gradient-to-br from-secondary-500 to-secondary-600 text-white' 
            : 'bg-gradient-to-br from-accent-500 to-accent-600 text-white'
        }`}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Trophy className="w-12 h-12" />
          </motion.div>

          <h1 className="text-3xl font-bold mb-2">
            {isPassed ? 'Congratulations!' : 'Keep Practicing!'}
          </h1>
          <p className="text-white/80 mb-6">
            {isPassed 
              ? 'You passed the exam! Great job!' 
              : 'You didn\'t pass this time, but keep trying!'}
          </p>

          <div className="text-6xl font-bold mb-2">{percentage}%</div>
          <div className="text-white/80 mb-6">
            {result.score} / {result.totalMarks} marks
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
            <div className="bg-white/20 rounded-xl p-3">
              <CheckCircle className="w-6 h-6 mx-auto mb-1" />
              <div className="text-lg font-bold">{correctAnswers}</div>
              <div className="text-xs text-white/70">Correct</div>
            </div>
            <div className="bg-white/20 rounded-xl p-3">
              <XCircle className="w-6 h-6 mx-auto mb-1" />
              <div className="text-lg font-bold">{totalQuestions - correctAnswers}</div>
              <div className="text-xs text-white/70">Wrong</div>
            </div>
            <div className="bg-white/20 rounded-xl p-3">
              <Clock className="w-6 h-6 mx-auto mb-1" />
              <div className="text-lg font-bold">{minutes}:{String(seconds).padStart(2, '0')}</div>
              <div className="text-xs text-white/70">Time</div>
            </div>
          </div>
        </div>
      </ScaleIn>

      {/* Stats */}
      <FadeIn delay={0.3}>
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6 mb-6">
          <h3 className="font-bold text-slate-900 mb-4">Performance Breakdown</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Score</span>
                <span className="font-semibold text-slate-900">{percentage}%</span>
              </div>
              <ProgressBar progress={percentage} />
            </div>
            
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Accuracy</span>
                <span className="font-semibold text-slate-900">
                  {totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0}%
                </span>
              </div>
              <ProgressBar 
                progress={totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0} 
              />
            </div>

            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Time Efficiency</span>
                <span className="font-semibold text-slate-900">
                  {exam ? Math.round((timeSpent / (exam.durationMinutes * 60)) * 100) : 0}%
                </span>
              </div>
              <ProgressBar 
                progress={exam ? Math.min((timeSpent / (exam.durationMinutes * 60)) * 100, 100) : 0} 
              />
            </div>
          </div>
        </div>
      </FadeIn>

      {/* Points Earned */}
      {result.pointsEarned && (
        <FadeIn delay={0.4}>
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-2xl p-6 mb-6 text-center"
          >
            <Zap className="w-8 h-8 mx-auto mb-2" />
            <div className="text-3xl font-bold">+{result.pointsEarned}</div>
            <div className="text-accent-100">Points Earned</div>
          </motion.div>
        </FadeIn>
      )}

      {/* Answer Review Toggle */}
      <FadeIn delay={0.5}>
        <button
          onClick={() => setShowAnswers(!showAnswers)}
          className="w-full flex items-center justify-between p-4 bg-white rounded-2xl shadow-lg border border-slate-200 mb-6 hover:bg-slate-50 transition-colors"
        >
          <span className="font-semibold text-slate-900">Review Answers</span>
          <motion.span
            animate={{ rotate: showAnswers ? 180 : 0 }}
            className="text-slate-400"
          >
            ▼
          </motion.span>
        </button>
      </FadeIn>

      {/* Answer Review */}
      {showAnswers && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-4 mb-6"
        >
          {result.answers?.map((answer: any, index: number) => (
            <AnimatedCard key={answer.id} className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <span className="text-sm text-slate-400 font-medium mb-1 block">
                    Question {index + 1}
                  </span>
                  <h4 className="font-semibold text-slate-900">
                    {answer.question?.questionTextBn || answer.question?.questionText}
                  </h4>
                </div>
                {answer.isCorrect ? (
                  <span className="flex-shrink-0 w-8 h-8 bg-secondary-100 text-secondary-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5" />
                  </span>
                ) : (
                  <span className="flex-shrink-0 w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                    <XCircle className="w-5 h-5" />
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'a', text: answer.question?.optionA },
                  { key: 'b', text: answer.question?.optionB },
                  { key: 'c', text: answer.question?.optionC },
                  { key: 'd', text: answer.question?.optionD },
                ].map((option) => (
                  <div
                    key={option.key}
                    className={`p-3 rounded-lg text-sm ${
                      option.key === answer.question?.correctOption
                        ? 'bg-secondary-100 text-secondary-700 border-2 border-secondary-500'
                        : option.key === answer.selectedOption && !answer.isCorrect
                          ? 'bg-red-100 text-red-700 border-2 border-red-500'
                          : 'bg-slate-50 text-slate-600'
                    }`}
                  >
                    <span className="font-bold mr-2">{option.key.toUpperCase()}.</span>
                    {option.text}
                    {option.key === answer.question?.correctOption && (
                      <CheckCircle className="w-4 h-4 inline ml-2" />
                    )}
                  </div>
                ))}
              </div>

              {answer.question?.explanation && (
                <div className="mt-4 p-3 bg-primary-50 rounded-lg text-sm text-primary-700">
                  <strong>Explanation:</strong> {answer.question.explanation}
                </div>
              )}
            </AnimatedCard>
          ))}
        </motion.div>
      )}

      {/* Actions */}
      <FadeIn delay={0.6}>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to={`/exams/${examId}`}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Exam
          </Link>
          <Link
            to="/exams"
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
          >
            <Home className="w-4 h-4" />
            More Exams
          </Link>
          <button
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'My Exam Result',
                  text: `I scored ${percentage}% on ${exam?.title}!`,
                  url: window.location.href,
                });
              } else {
                navigator.clipboard.writeText(`I scored ${percentage}% on ${exam?.title}!`);
                toast.success('Result copied to clipboard!');
              }
            }}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
      </FadeIn>
    </div>
  );
}
